import {
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";

import type { AssessmentProgress } from "@thalamos/haven-common";

/**
 * Every domain of an assessment being answered, with how far each has got
 * and the way into it, under a heading counting those complete. Where
 * `commentsMissingShown`, a domain with a rating lacking the comment it needs
 * is highlighted, saying how many.
 */
export const AssessmentDomainsTable = ({
  progress: { groups, completed, included },
  titleOf,
  domainPath,
  commentsMissingShown = false,
}: {
  progress: AssessmentProgress;
  /** The domain's title as the instrument words it. */
  titleOf: (group: string) => string;
  /** Where opening the domain goes. */
  domainPath: (group: string) => string;
  commentsMissingShown?: boolean;
}) => {
  const { t } = useTranslation();

  return (
    <>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="baseline"
      >
        <Typography variant="h5" component="h2">
          {t("assessment.overview.domains")}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t("assessment.overview.complete", { completed, included })}
        </Typography>
      </Stack>

      {groups.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t("assessment.overview.noDomains")}
        </Typography>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t("assessment.overview.domain")}</TableCell>
                <TableCell>{t("assessment.overview.status")}</TableCell>
                <TableCell>{t("assessment.overview.itemsActioned")}</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {groups.map(({ group, state, done, total, commentsMissing }) => {
                const needsComments =
                  commentsMissingShown && commentsMissing > 0;

                return (
                  <TableRow
                    key={group}
                    selected={state === "in_progress"}
                    sx={
                      needsComments
                        ? {
                            bgcolor: (theme) =>
                              alpha(theme.palette.warning.main, 0.12),
                          }
                        : undefined
                    }
                  >
                    <TableCell component="th" scope="row">
                      {titleOf(group)}
                    </TableCell>
                    <TableCell>
                      {t(`assessment.overview.state.${state}`)}
                      {needsComments ? (
                        <Typography
                          variant="body2"
                          color="warning.dark"
                          component="span"
                          sx={{ display: "block" }}
                        >
                          {t("assessment.overview.needComment", {
                            count: commentsMissing,
                          })}
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      {t("assessment.overview.itemsOf", { done, total })}
                    </TableCell>
                    <TableCell align="right">
                      <Link
                        component={RouterLink}
                        to={domainPath(group)}
                        aria-label={t("assessment.overview.openDomain", {
                          domain: titleOf(group),
                        })}
                      >
                        {t("assessment.overview.open")}
                      </Link>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </>
  );
};
